<template>
  <div class="festival-ops">
    <festival-ops-nav active-path="/festival/workbench" />
    <section class="festival-ops__panel">
      <div class="festival-ops__head">
        <div>
          <h2 class="festival-ops__title">节日专区运营概览</h2>
          <p class="festival-ops__muted">
            节日商品由专区独立维护，不关联其它业务板块商品。当前小区 {{ communityName }}
            <span class="festival-ops__id">{{ communityId }}</span>
          </p>
        </div>
        <div class="festival-ops__filter">
          <el-select v-model="clientType" size="small" @change="load">
            <el-option v-for="item in clientOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">刷新</el-button>
          <el-button size="small" icon="el-icon-edit-outline" @click="go('/festival/decorate')">去装修</el-button>
          <el-button type="primary" size="small" icon="el-icon-upload2" :loading="publishing" @click="publish">发布到用户端</el-button>
        </div>
      </div>
      <el-alert :title="statusText" :type="status.type" :closable="false" show-icon />
    </section>

    <section class="festival-ops__panel" v-loading="loading">
      <div class="festival-ops__cards">
        <div class="festival-ops__card" @click="go('/festival/decorate')">
          <span>活动状态</span>
          <strong>{{ status.label }}</strong>
        </div>
        <div class="festival-ops__card" @click="go('/festival/decorate')">
          <span>已发布版本</span>
          <strong>{{ version || 0 }}</strong>
        </div>
        <div class="festival-ops__card" @click="go('/festival/products')">
          <span>商品库 / 已上架</span>
          <strong>{{ productTotal }} / {{ onShelfCount }}</strong>
        </div>
        <div class="festival-ops__card" @click="go('/festival/products')">
          <span>可跳转商品</span>
          <strong>{{ jumpableCount }}</strong>
        </div>
      </div>
    </section>

    <section class="festival-ops__panel" v-loading="loading">
      <div class="festival-ops__head">
        <h2 class="festival-ops__title">当前专题</h2>
      </div>
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item label="专题名称">{{ configName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="专题开关">{{ enabled ? '已启用' : '已关闭' }}</el-descriptions-item>
        <el-descriptions-item label="生效时间">{{ startTime || '未设置' }}</el-descriptions-item>
        <el-descriptions-item label="结束时间">{{ endTime || '未设置' }}</el-descriptions-item>
        <el-descriptions-item label="最近发布">{{ publishTime || '尚未发布' }}</el-descriptions-item>
        <el-descriptions-item label="推荐商品">已编排 {{ recommendedCount }} 个</el-descriptions-item>
      </el-descriptions>
      <p class="festival-ops__muted">
        用户端优先展示节日专区自营商品，价格、库存、详情和订单均由节日专区维护。
      </p>
    </section>
  </div>
</template>

<script>
import { getCommunity, getCommunityId } from '@/api/community/communityApi'
import { getFestivalConfig, listFestivalProducts, publishFestivalConfig, saveFestivalDraft } from '@/api/life/festivalApi'
import FestivalOpsNav from './festivalNav.vue'
import { CLIENT_OPTIONS, campaignStatus, createDefaultConfig, isJumpableProduct, normalizePageConfig } from './festivalConfig'
import './festival-ops.scss'

export default {
  name: 'FestivalWorkbench',
  components: { FestivalOpsNav },
  data() {
    return {
      loading: false,
      publishing: false,
      clientType: 'ALL',
      clientOptions: CLIENT_OPTIONS,
      configName: '',
      version: 0,
      enabled: false,
      startTime: '',
      endTime: '',
      publishTime: '',
      draft: createDefaultConfig(),
      published: {},
      products: [],
      productTotal: 0
    }
  },
  computed: {
    communityId() { return getCommunityId() || '-1' },
    communityName() { const current = getCommunity() || {}; return current.name || '物业公司默认范围' },
    status() {
      return campaignStatus({
        enabled: this.enabled,
        version: this.version,
        startTime: this.startTime,
        endTime: this.endTime,
        published: this.published
      })
    },
    statusText() {
      if (this.status.code === 'UNPUBLISHED') return '专题尚未发布，用户端不会展示可成交内容'
      if (this.status.code === 'OFFLINE') return '专题已关闭，用户端读取为下线状态'
      if (this.status.code === 'UPCOMING') return `专题未开始，生效时间 ${this.startTime}`
      if (this.status.code === 'ENDED') return `专题已结束，结束时间 ${this.endTime}`
      return `专题进行中，用户端读取已发布 v${this.version}`
    },
    onShelfCount() { return this.products.filter(item => item.enabled === 'Y').length },
    jumpableCount() { return this.products.filter(isJumpableProduct).length },
    recommendedCount() { return ((this.draft.recommendation && this.draft.recommendation.productIds) || []).length }
  },
  created() { this.load() },
  methods: {
    go(path) { this.$router.push({ path }) },
    async load() {
      this.loading = true
      try {
        const [configRes, productRes] = await Promise.all([
          getFestivalConfig({ communityId: this.communityId, clientType: this.clientType }),
          listFestivalProducts({ page: 1, row: 200 })
        ])
        const row = configRes.data || {}
        this.configName = row.configName || ''
        this.version = Number(row.version || 0)
        this.enabled = row.enabled === 'Y'
        this.startTime = row.startTime || ''
        this.endTime = row.endTime || ''
        this.publishTime = row.publishTime || ''
        this.draft = normalizePageConfig(row.draft && Object.keys(row.draft).length ? row.draft : {})
        this.published = row.published || {}
        if (row.enabled) this.draft.enabled = row.enabled === 'Y'
        this.products = productRes.data || []
        this.productTotal = Number(productRes.total || this.products.length)
      } catch (error) {
        this.$message.error(error.message || '加载节日专区概览失败')
      } finally {
        this.loading = false
      }
    },
    payload() {
      return {
        communityId: this.communityId,
        clientType: this.clientType,
        configName: this.configName || '节日专区',
        startTime: this.startTime,
        endTime: this.endTime,
        enabled: this.enabled,
        config: this.draft
      }
    },
    async publish() {
      try {
        await this.$confirm('发布后用户端将读取当前草稿配置，确认继续？', '发布节日专区', { type: 'warning' })
        this.publishing = true
        await saveFestivalDraft(this.payload())
        const res = await publishFestivalConfig(this.payload())
        this.version = Number(res.data && res.data.version || this.version + 1)
        this.$message.success('节日专区已发布到用户端')
        await this.load()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '发布失败')
      } finally {
        this.publishing = false
      }
    }
  }
}
</script>
